import axios, { AxiosResponse } from 'axios';
import {
  AcademicSettingsData,
  ConfiguredEOASchool,
  DemographicSettingsData,
  SchoolConfigData,
  DSASettingsData,
  SubjectArea,
  SubjectAreaNames,
  DemographicCategoryOption,
  SchoolConfigEntry,
  SchoolConfigPatchOp,
  GetCollegeEligibilityFrameworksResponse,
} from '../interfaces/EOAInterface';
import { Operation } from 'fast-json-patch';

const BASE_URL = process.env.REACT_APP_BASE_URL;

const getConfiguredSchools = async (eoaId: string, access_token: string, includeNonPrimary = false) => {
  return await axios.get<ConfiguredEOASchool[]>(`${BASE_URL}/eoa/${eoaId}/schools`, {
    headers: {
      Authorization: `Bearer ${access_token}`,
    },
    params: includeNonPrimary ? { include_non_primary: true } : undefined,
  });
};

const configureSchool = async (eoaId: string, access_token: string, schoolData: SchoolConfigData | object) => {
  return await axios.post(`${BASE_URL}/eoa/${eoaId}/schools`, schoolData, {
    headers: {
      Authorization: `Bearer ${access_token}`,
    },
  });
};

const editConfiguredSchool = async (
  eoaId: string,
  access_token: string,
  school_id: string,
  patchOperations: Operation[]
) => {
  return await axios.patch(`${BASE_URL}/eoa/${eoaId}/schools/${school_id}`, patchOperations, {
    headers: {
      Authorization: `Bearer ${access_token}`,
    },
  });
};

const deleteConfiguredSchool = async (eoaId: string, access_token: string, school_id: string) => {
  return await axios.delete(`${BASE_URL}/eoa/${eoaId}/schools/${school_id}`, {
    headers: {
      Authorization: `Bearer ${access_token}`,
    },
  });
};

const saveAcademicSettings = async (
  eoaId: string,
  access_token: string,
  academicSettingsData: AcademicSettingsData
) => {
  return await axios.post(`${BASE_URL}/eoa/${eoaId}/academic-settings`, academicSettingsData, {
    headers: {
      Authorization: `Bearer ${access_token}`,
    },
  });
};

const getSubjectAreas = async (access_token: string, subjectArea?: SubjectAreaNames) => {
  return await axios.get<SubjectArea[]>(`${BASE_URL}/subject-areas`, {
    headers: {
      Authorization: `Bearer ${access_token}`,
    },
    params: subjectArea ? { subject_area: subjectArea } : undefined,
  });
};

const saveDSASettings = async (access_token: string, eoaId: string, DSASettings: DSASettingsData) => {
  return await axios.post(`${BASE_URL}/eoa/${eoaId}/dsa-settings`, DSASettings, {
    headers: {
      Authorization: `Bearer ${access_token}`,
    },
  });
};

const configureDemographicCategories = async (eoaId: string, access_token: string, data: DemographicSettingsData) => {
  return await axios.post(`${BASE_URL}/eoa/${eoaId}/demographics`, data, {
    headers: {
      Authorization: `Bearer ${access_token}`,
    },
  });
};

export type State = {
  state_id: string;
  name: string;
  short_name: string;
};

export interface OewsAreaTitlesResponse {
  area_titles: string[];
}

const getOewsAreaTitles = async (eoaId: string, access_token: string) => {
  return await axios.get<OewsAreaTitlesResponse>(`${BASE_URL}/eoa/${eoaId}/oews-area-titles`, {
    headers: {
      Authorization: `Bearer ${access_token}`,
    },
  });
};

let cachedStates: State[] | null = null;
let statesRequest: Promise<AxiosResponse<State[]>> | null = null;

const getCachedStates = (): State[] => cachedStates ?? [];

const getStates = async (eoaId: string, access_token: string) => {
  if (cachedStates) return { data: cachedStates } as AxiosResponse<State[]>;
  if (!statesRequest) {
    statesRequest = axios
      .get<State[]>(`${BASE_URL}/states`, {
        headers: {
          Authorization: `Bearer ${access_token}`,
        },
      })
      .then((response) => {
        cachedStates = response.data;
        return response;
      })
      .catch((error) => {
        statesRequest = null;
        throw error;
      });
  }
  return statesRequest;
};

const getDemographicsCategories = async (eoaId: string, access_token: string) => {
  return await axios.get<DemographicCategoryOption[]>(`${BASE_URL}/eoa/${eoaId}/demographic-categories`, {
    headers: {
      Authorization: `Bearer ${access_token}`,
    },
  });
};

export interface EoaDistrictPayload {
  district_name?: string | null;
  district_state_id?: string | null;
  district_aliases?: string[];
  district_sis?: string | null;
  district_lms?: string | null;
  is_dsa_signed?: boolean | null;
  year_ending?: number | null;
}

export interface CreateEoaDraftBody extends Omit<EoaDistrictPayload, 'year_ending'> {
  can_provide_files?: string;
  year_ending?: number;
  academic_period_names?: string[];
  is_test?: boolean;
}

export interface EoaAcademicConfigurationPayload {
  academic_term_count?: number | null;
  academic_period_names?: string[];
  academic_period_aliases?: Record<string, string[]>;
  significant_cross_school_enrollment?: string;
  how_are_student_transfers_handled?: string;
  how_do_dual_enrollment_courses_appear?: string;
}

export interface EoaGradingConfigurationPayload {
  default_number_of_credits?: string | null;
  grading_system?: string | null;
  failing_grade_numeric_threshold?: number | null;
  failing_grade_letter_threshold?: string | null;
  credit_hours_map_to_carnegie?: string | null;
  letter_to_number_grade_map?: Record<string, number>;
  multiple_credit_systems_in_transcripts?: string | null;
}

export interface EoaInfoConfigurationPayload {
  year_ending?: number | null;
  sjw_date?: string | null;
  min_demographic_bucket_size?: number | null;
  district_data_leads?: RoleAssignment[];
  lead_facilitators?: RoleAssignment[];
  facilitators?: RoleAssignment[];
}

export interface RoleAssignment {
  user_id: string | null;
  name: string;
  email: string;
}

export interface SurveyLanguageOption {
  id: string;
  name: string;
}

export interface EoaSurveyConfigurationPayload {
  survey_language_ids?: string[] | null;
  survey_languages?: SurveyLanguageOption[] | null;
  staff_role_names?: string[] | null;
  estimated_families_per_school?: number | null;
  estimated_students_per_school?: number | null;
  estimated_educators_per_school?: number | null;
}

export interface CollegeEligibilityFrameworkWriteBody {
  college_name?: string | null;
  requirements?: Record<string, string>;
  is_main_requirement?: boolean;
  is_common_local_university?: boolean;
  display_order?: number;
}

const authHeaders = (access_token: string) => ({
  Authorization: `Bearer ${access_token}`,
});

export const patchEoaSchool = async (eoaId: string, school_id: string, access_token: string, requestData: object) => {
  return await axios.patch(`${BASE_URL}/eoa/${eoaId}/schools/${school_id}`, requestData, {
    headers: authHeaders(access_token),
  });
};

export const createEoaDraft = async (access_token: string, requestData: EoaDistrictPayload, signal?: AbortSignal) => {
  return await axios.post<string | { id: string; eoa_id?: string }>(`${BASE_URL}/eoa`, requestData, {
    headers: authHeaders(access_token),
    signal,
  });
};

export const finalizeEoa = async (eoaId: string, access_token: string) => {
  return await axios.post(`${BASE_URL}/eoa/${eoaId}/finalize`, undefined, {
    headers: authHeaders(access_token),
  });
};

export const patchEoa = async (eoaId: string, access_token: string, requestData: EoaDistrictPayload) => {
  return await axios.patch(`${BASE_URL}/eoa/${eoaId}`, requestData, {
    headers: authHeaders(access_token),
  });
};

export const getEoa = async (eoaId: string, access_token: string) => {
  return await axios.get<Partial<EoaDistrictPayload> & { id?: string }>(`${BASE_URL}/eoa/${eoaId}`, {
    headers: authHeaders(access_token),
  });
};

export const getEoaAcademicConfiguration = async (eoaId: string, access_token: string) => {
  return await axios.get<EoaAcademicConfigurationPayload>(`${BASE_URL}/eoa/${eoaId}/academic-settings`, {
    headers: authHeaders(access_token),
  });
};

export const saveEoaAcademicConfiguration = async (
  eoaId: string,
  access_token: string,
  requestData: EoaAcademicConfigurationPayload
) => {
  return await axios.post(`${BASE_URL}/eoa/${eoaId}/academic-settings`, requestData, {
    headers: authHeaders(access_token),
  });
};

export const patchEoaAcademicConfiguration = async (
  eoaId: string,
  access_token: string,
  requestData: EoaAcademicConfigurationPayload
) => {
  return await axios.patch(`${BASE_URL}/eoa/${eoaId}/academic-settings`, requestData, {
    headers: authHeaders(access_token),
  });
};

export const getEoaGradingConfiguration = async (eoaId: string, access_token: string) => {
  return await axios.get<EoaGradingConfigurationPayload>(`${BASE_URL}/eoa/${eoaId}/grading-settings`, {
    headers: authHeaders(access_token),
  });
};

export const saveEoaGradingConfiguration = async (
  eoaId: string,
  access_token: string,
  requestData: EoaGradingConfigurationPayload
) => {
  return await axios.post(`${BASE_URL}/eoa/${eoaId}/grading-settings`, requestData, {
    headers: authHeaders(access_token),
  });
};

export const patchEoaGradingConfiguration = async (
  eoaId: string,
  access_token: string,
  requestData: EoaGradingConfigurationPayload
) => {
  return await axios.patch(`${BASE_URL}/eoa/${eoaId}/grading-settings`, requestData, {
    headers: authHeaders(access_token),
  });
};

export const getEoaInfoConfiguration = async (eoaId: string, access_token: string) => {
  return await axios.get<EoaInfoConfigurationPayload>(`${BASE_URL}/eoa/${eoaId}/eoa-config`, {
    headers: authHeaders(access_token),
  });
};

export const saveEoaInfoConfiguration = async (
  eoaId: string,
  access_token: string,
  requestData: EoaInfoConfigurationPayload
) => {
  return await axios.post(`${BASE_URL}/eoa/${eoaId}/eoa-config`, requestData, {
    headers: authHeaders(access_token),
  });
};

export const patchEoaInfoConfiguration = async (
  eoaId: string,
  access_token: string,
  requestData: EoaInfoConfigurationPayload
) => {
  return await axios.patch(`${BASE_URL}/eoa/${eoaId}/eoa-config`, requestData, {
    headers: authHeaders(access_token),
  });
};

const surveyLanguagesFromResponse = (data: unknown): SurveyLanguageOption[] => {
  if (!Array.isArray(data)) return [];
  return data.flatMap((item) => {
    if (!item || typeof item !== 'object') return [];
    const language = item as { id?: unknown; name?: unknown };
    if (language.id == null || typeof language.name !== 'string' || !language.name.trim()) return [];
    return [{ id: String(language.id), name: language.name.trim() }];
  });
};

export const getSurveyLanguages = async (eoaId: string, access_token: string) => {
  const response = await axios.get<unknown>(`${BASE_URL}/eoa/${eoaId}/survey-languages`, {
    headers: authHeaders(access_token),
  });
  return { ...response, data: surveyLanguagesFromResponse(response.data) };
};

export const getEoaSurveyConfiguration = async (eoaId: string, access_token: string) => {
  return await axios.get<EoaSurveyConfigurationPayload>(`${BASE_URL}/eoa/${eoaId}/survey-config`, {
    headers: authHeaders(access_token),
  });
};

export const saveEoaSurveyConfiguration = async (
  eoaId: string,
  access_token: string,
  requestData: EoaSurveyConfigurationPayload
) => {
  return await axios.post(`${BASE_URL}/eoa/${eoaId}/survey-config`, requestData, {
    headers: authHeaders(access_token),
  });
};

export const patchEoaSurveyConfiguration = async (
  eoaId: string,
  access_token: string,
  requestData: EoaSurveyConfigurationPayload
) => {
  return await axios.patch(`${BASE_URL}/eoa/${eoaId}/survey-config`, requestData, {
    headers: authHeaders(access_token),
  });
};

type CreateEOAABody = {
  district_name: string;
  graduating_year: number;
  academic_period_names: string[];
  academic_period_aliases: {
    [key: string]: string[];
  };
  test_types: string[];
  demographics: string[];
};

const createEOA = async (access_token: string, requestData: CreateEOAABody) => {
  return await axios.post(`${BASE_URL}/eoa`, requestData, {
    headers: {
      Authorization: `Bearer ${access_token}`,
    },
  });
};

const getEOAConfig = async (eoaId: string, access_token: string) => {
  return await axios.get(`${BASE_URL}/eoa/${eoaId}`, {
    headers: {
      Authorization: `Bearer ${access_token}`,
    },
  });
};

const getSchoolsConfig = async (eoaId: string, access_token: string) => {
  return await axios.get<SchoolConfigEntry[]>(`${BASE_URL}/eoa/${eoaId}/schools-config`, {
    headers: {
      Authorization: `Bearer ${access_token}`,
    },
  });
};

const patchSchoolConfig = async (
  eoaId: string,
  schoolId: string,
  access_token: string,
  operations: SchoolConfigPatchOp[]
) => {
  return await axios.patch<SchoolConfigEntry>(`${BASE_URL}/eoa/${eoaId}/schools/${schoolId}/config`, operations, {
    headers: {
      Authorization: `Bearer ${access_token}`,
    },
  });
};

const getCollegeEligibilityFrameworks = async (eoaId: string, access_token: string) => {
  return await axios.get<GetCollegeEligibilityFrameworksResponse>(
    `${BASE_URL}/eoa/${eoaId}/college-eligibility-frameworks`,
    {
      headers: {
        Authorization: `Bearer ${access_token}`,
      },
    }
  );
};

export const saveEoaCollegeEligibility = async (
  eoaId: string,
  access_token: string,
  requestData: CollegeEligibilityFrameworkWriteBody
) => {
  return await axios.post<string>(`${BASE_URL}/eoa/${eoaId}/college-eligibility-frameworks`, requestData, {
    headers: authHeaders(access_token),
  });
};

export const patchEoaCollegeEligibility = async (
  eoaId: string,
  frameworkId: string,
  access_token: string,
  requestData: CollegeEligibilityFrameworkWriteBody
) => {
  return await axios.patch(`${BASE_URL}/eoa/${eoaId}/college-eligibility-frameworks/${frameworkId}`, requestData, {
    headers: authHeaders(access_token),
  });
};

export {
  getConfiguredSchools,
  configureSchool,
  editConfiguredSchool,
  deleteConfiguredSchool,
  saveAcademicSettings,
  getSubjectAreas,
  saveDSASettings,
  configureDemographicCategories,
  getOewsAreaTitles,
  getCachedStates,
  getStates,
  getDemographicsCategories,
  createEOA,
  getEOAConfig,
  getSchoolsConfig,
  patchSchoolConfig,
  getCollegeEligibilityFrameworks,
};
